import "@src/pages/formDrawer/drawer.css";
import "./onboarding.css";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { Input, Select, Switch } from "antd";
import { CaretDownFilled } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import Spinner from "@src/components/spinner";
import { HelpLink } from "@src/components/helpLink/helpLink";
import { currencyOptions } from "@src/util/currency/currency";
import { companyOnboardingQuery, saveOnboardingStepMutation, completeOnboardingMutation } from "@src/util/query/mobileOfficer.query";
import logo from "@src/assets/img/wc-logo-big.png";

// The walk-through a new company's first super admin gets on first login, full screen (outside
// the portal's navbar): one step at a time, each saved as they go (wfc-server
// OnboardingService), so "Finish later" keeps their place and the dashboard shows what's left.
export const Onboarding = () => {
    const navigate = useNavigate();
    const { data, loading, refetch } = useQuery(companyOnboardingQuery, { fetchPolicy: "network-only" });
    const [saveStep, { loading: saving }] = useMutation(saveOnboardingStepMutation);
    const [complete, { loading: completing }] = useMutation(completeOnboardingMutation);
    const ob = data?.companyOnboarding;
    const steps = ob?.steps || [];
    const [chosen, setIndex] = useState(null);
    const [draft, setDraft] = useState({});
    const [error, setError] = useState(null);

    // until they move, the first step not done yet
    const firstOpen = steps.findIndex((s) => !s.done);
    const index = chosen ?? (firstOpen === -1 ? steps.length : firstOpen);
    const step = steps[index];
    const value = (group, field) => (draft[group]?.[field] !== undefined ? draft[group][field] : ob?.[group]?.[field]);
    const set = (group, field) => (v) => setDraft({ ...draft, [group]: { ...(draft[group] || {}), [field]: v?.target ? v.target.value : v } });
    const modules = useMemo(() => (ob?.features || []).filter((f) => f.kind === "module"), [ob]);

    const go = (i) => { setError(null); setIndex(i); window.scrollTo(0, 0); };
    const next = async () => {
        setError(null);
        try {
            await saveStep({ variables: { step: step.key, data: draft[step.key] || {} } });
            await refetch();
            setDraft({ ...draft, [step.key]: undefined });
            go(index + 1);
        } catch (err) {
            setError(err?.message || "That step can't be saved right now");
        }
    };
    const finish = async () => {
        await complete();
        navigate("/user/dashboard");
    };

    const done = !!ob && index >= steps.length;
    const skipped = steps.filter((s) => !s.done);
    // the bar moves with where they are, not only with what's saved
    const progress = steps.length ? Math.round((Math.min(index, steps.length) / steps.length) * 100) : 0;

    return (
        <div className="ob">
            <div className="ob-sky" aria-hidden="true"><i /><i /><i /></div>
            <div className="ob-bar" aria-hidden="true"><i style={{ width: `${done ? 100 : progress}%` }} /></div>

            <header className="ob-top">
                <div className="ob-logo"><img src={logo} alt="" /><span>WFC360</span></div>
                {!done && <button type="button" className="ob-link" onClick={() => navigate("/user/dashboard")}>Finish later</button>}
            </header>

            <main className="ob-stage">
                {loading && !ob ? <Spinner /> : !ob ? null : done ? (
                    <section key="done" className="ob-card ob-done">
                        <svg className="ob-tick" viewBox="0 0 52 52" aria-hidden="true">
                            <circle cx="26" cy="26" r="24" />
                            <path d="M15 27l7 7 15-15" />
                        </svg>
                        <h1>{skipped.length ? "Nearly there" : "You're set up"}</h1>
                        <p>{skipped.length
                            ? <>You skipped {skipped.map((s) => s.title).join(", ")}. Finish later and the dashboard keeps a reminder, or finish now and set {skipped.length === 1 ? "it" : "them"} up any time in Settings.</>
                            : <>Everything you chose is saved. You can change any of it later in Settings, and <b>Settings › Your setup</b> shows what's switched on and what each part does.</>}</p>
                        <ul className="ob-done-list">
                            {steps.map((s) => <li key={s.key} className={s.done ? "" : "ob-skipped"}>{s.done ? <FontAwesomeIcon icon={faCheck} /> : <span className="ob-dash" />}{s.title}{!s.done && " (skipped)"}</li>)}
                        </ul>
                        <div className="ob-done-actions">
                            {skipped.length > 0 && <button type="button" className="drawer-outlined-btn" onClick={() => navigate("/user/dashboard")}>Finish later</button>}
                            <button type="button" className="drawer-filled-btn ob-go" onClick={finish} disabled={completing}>{skipped.length ? "Finish setup" : "Go to my dashboard"}</button>
                        </div>
                    </section>
                ) : (
                    <section key={step.key} className="ob-card">
                        <div className="ob-head">
                            <div className="ob-count">Step {index + 1} of {steps.length}</div>
                            <h1>{index === 0 ? `Welcome. Let's set up ${ob.company?.name || "your company"}` : step.title}</h1>
                            <p>{index === 0 ? "A few minutes now saves time later. Each step saves as you go, and you can change anything later in Settings." : step.summary}</p>
                        </div>

                        {step.key === "company" && (
                            <div className="ob-form">
                                <label>Company name<Input className="generic-input-control" value={ob.company.name} readOnly /><small>Set when WFC360 created your account. Ask us to change it.</small></label>
                                <label>Legal name<Input className="generic-input-control" value={value("company", "legalname")} onChange={set("company", "legalname")} placeholder="e.g. Kingsgate Security Ltd" /></label>
                                <label>Company number<Input className="generic-input-control" value={value("company", "regno")} onChange={set("company", "regno")} placeholder="e.g. 12345678" /></label>
                                <label>Phone<Input className="generic-input-control" value={value("company", "tel")} onChange={set("company", "tel")} placeholder="e.g. 020 7946 0000" /></label>
                                <label className="ob-wide">Address<Input.TextArea className="generic-input-control" rows={3} value={value("company", "address")} onChange={set("company", "address")} /><small>Shown on invoices and on officers' ID cards in the app.</small></label>
                            </div>
                        )}

                        {step.key === "money" && (
                            <div className="ob-form">
                                <label className="ob-wide">Default currency
                                    <Select className="custom-select" showSearch optionFilterProp="label" suffixIcon={<CaretDownFilled />} value={value("money", "invoiceCurrency")} onChange={set("money", "invoiceCurrency")} options={currencyOptions()} />
                                    <small>Every rate, report and invoice without its own currency uses this one. Currencies are never converted.</small>
                                </label>
                                <label>Invoice number prefix<Input className="generic-input-control" value={value("money", "invoicePrefix")} onChange={set("money", "invoicePrefix")} placeholder="e.g. INV" /></label>
                                <label>VAT number<Input className="generic-input-control" value={value("money", "vatNumber")} onChange={set("money", "vatNumber")} placeholder="e.g. GB 345 6242 03" /></label>
                                <label className="ob-wide">Payment terms<Input className="generic-input-control" value={value("money", "paymentTerms")} onChange={set("money", "paymentTerms")} placeholder="e.g. 30 days" /></label>
                            </div>
                        )}

                        {step.key === "services" && (
                            <div className="ob-list">
                                {modules.map((m) => (
                                    <div key={m.key} className="ob-row">
                                        <div><b>{m.name}</b><span>{m.summary}</span></div>
                                        <span className={`ob-pill${m.value ? " ob-pill-on" : ""}`}>{m.value ? "On" : "Off"}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {step.key === "services" && <p className="ob-foot-note" style={{ marginTop: -14 }}>WFC360 switches these on for you. To add one, contact us.</p>}

                        {step.key === "mobile" && (
                            <div className="ob-mobile">
                                <div className="ob-list">
                                    <div className="ob-row"><div><b>Shift run</b><span>Scheduled on your office site. They pick keys at Clock In and get jobs during the shift.</span></div></div>
                                    <div className="ob-row"><div><b>Direct jobs</b><span>No office shift. Control schedules their mobile jobs, often until further notice.</span></div></div>
                                    <div className="ob-row"><div><b>Callouts</b><span>They take a call and start the job themselves. The system picks the report.</span></div></div>
                                </div>
                                <p className="ob-foot-note">Use any mix. Each officer is set up on their profile, under Employee App Access › Mobile Driver.</p>

                                <div className="ob-list">
                                    <div className="ob-row">
                                        <div><b>Send new jobs to the officer holding the keys</b><span>A job nobody was picked for goes to the officer on shift who has that site's keys. Off: it waits for you.</span></div>
                                        <Switch checked={value("mobile", "autoSendToKeyHolder")} onChange={set("mobile", "autoSendToKeyHolder")} />
                                    </div>
                                    <div className="ob-row">
                                        <div><b>Officers can start callouts</b><span>Mobile drivers with "Can start callouts" ticked see Start a Callout in the app. Off: only control creates jobs.</span></div>
                                        <Switch checked={value("mobile", "callouts")} onChange={set("mobile", "callouts")} />
                                    </div>
                                </div>

                                <div>
                                    <div className="ob-sub">Report form for each job type</div>
                                    <div className="ob-list">
                                        {(ob.mobile?.jobTypes || []).map((t) => (
                                            <div key={t._id} className="ob-row">
                                                <div><b>{t.name}</b></div>
                                                <Select className="custom-select" style={{ width: 280 }} allowClear placeholder="Choose a report form" suffixIcon={<CaretDownFilled />}
                                                    value={(draft.mobile?.reportForms?.[t._id]) ?? t.reportTemplateId ?? undefined}
                                                    onChange={(v) => setDraft({ ...draft, mobile: { ...(draft.mobile || {}), reportForms: { ...(draft.mobile?.reportForms || {}), [t._id]: v || null } } })}
                                                    options={(ob.mobile?.reportTemplates || []).map((x) => ({ value: x._id, label: x.name }))}
                                                    notFoundContent="No report forms yet: add them in Settings › Report management" />
                                            </div>
                                        ))}
                                    </div>
                                    <p className="ob-foot-note">Used for callouts, and to start each new job. You can change these later in Settings › Mobile Services.</p>
                                </div>
                            </div>
                        )}

                        {step.key === "team" && (
                            <div className="ob-team">
                                <div className="ob-team-count"><b>{ob.team?.users || 0}</b> {ob.team?.users === 1 ? "person has" : "people have"} a login so far</div>
                                <p>Invite the people who'll run WFC360 with you: control room staff, managers, payroll. Each gets a role that decides what they see. Officers don't need a login here: they use the app.</p>
                                <div className="ob-team-actions">
                                    <a className="drawer-outlined-btn" href="/user/dashboard#/setting/adduser" target="_blank" rel="noopener noreferrer">Invite people</a>
                                    <HelpLink slug="setup-users-roles">Users and roles</HelpLink>
                                </div>
                            </div>
                        )}

                        {error && <div className="ob-error" role="alert">{error}</div>}

                        <div className="ob-foot">
                            {index > 0 ? <button type="button" className="drawer-outlined-btn" onClick={() => go(index - 1)}>Back</button> : <span />}
                            <div className="ob-foot-right">
                                {/* not now: move on without saving this step - it stays on the dashboard checklist */}
                                <button type="button" className="ob-link" onClick={() => go(index + 1)} disabled={saving}>Skip for now</button>
                                <button type="button" className={saving ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} onClick={next} disabled={saving}>{saving ? "Saving…" : index === steps.length - 1 ? "Save and finish" : "Save and continue"}</button>
                            </div>
                        </div>
                    </section>
                )}

                {!done && ob && (
                    <ol className="ob-dots" aria-label={`Step ${index + 1} of ${steps.length}`}>
                        {steps.map((s, i) => (
                            <li key={s.key}>
                                <button type="button" title={s.title} aria-label={s.title} className={`${i === index ? "ob-dot-on" : ""}${s.done ? " ob-dot-done" : ""}`} onClick={() => go(i)} />
                            </li>
                        ))}
                    </ol>
                )}
            </main>
        </div>
    );
};

// Dashboard: the steps left, until the walk-through is finished
export const SetupChecklist = () => {
    const navigate = useNavigate();
    const { data } = useQuery(companyOnboardingQuery, { fetchPolicy: "cache-and-network" });
    const ob = data?.companyOnboarding;
    // a brand-new company's super admin goes straight to the walk-through on first login
    useEffect(() => {
        if (!ob?.show) return;
        let seen = null;
        try { seen = sessionStorage.getItem("onboardingSeen"); } catch { /* storage blocked */ }
        if (!seen && !ob.steps.some((s) => s.done)) {
            try { sessionStorage.setItem("onboardingSeen", "1"); } catch { /* storage blocked */ }
            navigate("/user/setup");
        }
    }, [ob]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!ob?.show) return null;
    const left = ob.steps.filter((s) => !s.done);
    return (
        <div className="ob-check">
            <div className="ob-check-text">
                <b>Finish setting up {ob.company?.name}</b>
                <span>{ob.steps.length - left.length} of {ob.steps.length} done{left.length ? ` · next: ${left[0].title}` : ""}</span>
            </div>
            <div className="ob-check-steps">
                {ob.steps.map((s) => <span key={s.key} className={s.done ? "ob-check-done" : ""}>{s.done && <FontAwesomeIcon icon={faCheck} />} {s.title}</span>)}
            </div>
            <button type="button" className="drawer-filled-btn" onClick={() => navigate("/user/setup")}>{left.length ? "Continue setup" : "Finish"}</button>
        </div>
    );
};
